import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import LytIcon from '../icon/LytIcon';
import { ICON_NAMES } from '../icon/icon-constants';

/**
 * Badge type/variant constants
 * 7 variants as per Figma design system
 */
export const BADGE_TYPES = {
    DEFAULT: 'default',
    INVERTED_DEFAULT: 'inverted-default',
    PRIMARY: 'primary',
    INVERTED_PRIMARY: 'inverted-primary',
    SUCCESS: 'success',
    WARNING: 'warning',
    IMPORTANT: 'important',
};

/**
 * Get badge colors for each variant from theme
 * Falls back to hardcoded values if theme not available
 */
const getBadgeColors = (variant, theme) => {
    const badgeTheme = theme?.components?.lyt_badge?.[variant];
    if (badgeTheme) {
        return {
            background: badgeTheme.background,
            text: badgeTheme.text,
            border: badgeTheme.border,
        };
    }

    // Fallback colors (light theme defaults)
    const fallbacks = {
        [BADGE_TYPES.DEFAULT]: {
            background: '#F0F2F9',
            text: '#474749',
            border: 'transparent',
        },
        [BADGE_TYPES.INVERTED_DEFAULT]: {
            background: '#FFFFFF',
            text: '#474749',
            border: '#DDDFE5',
        },
        [BADGE_TYPES.PRIMARY]: {
            background: '#0063FF',
            text: '#F8F9FC',
            border: 'transparent',
        },
        [BADGE_TYPES.INVERTED_PRIMARY]: {
            background: '#FFFFFF',
            text: '#0063FF',
            border: '#0063FF',
        },
        [BADGE_TYPES.SUCCESS]: {
            background: '#C6FFD9',
            text: '#002F1C',
            border: 'transparent',
        },
        [BADGE_TYPES.WARNING]: {
            background: '#FFFBC1',
            text: '#2E2904',
            border: 'transparent',
        },
        [BADGE_TYPES.IMPORTANT]: {
            background: '#F9E2E1',
            text: '#761E19',
            border: 'transparent',
        },
    };
    return fallbacks[variant] || fallbacks[BADGE_TYPES.DEFAULT];
};
const StyledBadge = styled.div.withConfig({
    shouldForwardProp: (prop) => !['badgeType', 'hasLabel'].includes(prop),
})`
    /* Badge typography: IBM Plex Sans, 12px, SemiBold, uppercase for labels */
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 9px;
    text-transform: uppercase;
    letter-spacing: 0;

    display: inline-flex;
    min-width: 24px;
    min-height: 20px;
    padding: 4px 6px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 100px;

    /* Dynamic colors based on variant */
    ${({ theme, badgeType }) => {
        const colors = getBadgeColors(badgeType, theme);
        return css`
            background: ${colors.background};
            color: ${colors.text};
            border: 1px solid ${colors.border};
        `;
    }}

    /* Ensure consistent sizing */
    box-sizing: border-box;
    white-space: nowrap;

    /* Prevent text selection */
    user-select: none;

    /* Icon styling */
    svg {
        flex-shrink: 0;
        width: 12px !important;
        height: 12px !important;
    }
`;

/**
 * Formats a number for badge display
 * Numbers greater than 99 are displayed as "99+"
 * @param {number|string} value - The value to format
 * @returns {string} The formatted display value
 */
const formatBadgeValue = (value) => {
    // If value is null/undefined, return '0'
    if (value === null || value === undefined) {
        return '0';
    }
    const numValue = parseInt(value, 10);

    // Handle invalid numbers
    if (isNaN(numValue)) {
        return '0';
    }

    // Handle negative numbers
    if (numValue < 0) {
        return '0';
    }

    // Handle numbers greater than 99
    if (numValue > 99) {
        return '99+';
    }
    return numValue.toString();
};

/**
 * LytBadge component - A visual indicator for status labels or numeric values
 *
 * Supports two modes:
 * 1. Label mode: Display text labels (e.g., "NEW", "APPROVED", "PENDING")
 * 2. Value mode: Display numeric values with auto-formatting (max 99+)
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Text label to display (takes precedence over value)
 * @param {number|string} props.value - Numeric value to display (used if no label)
 * @param {string} props.type - Badge variant (default, inverted-default, primary, inverted-primary, success, warning, important)
 * @param {string} props.leftIcon - Optional left icon name from LytIcon
 * @param {string} props.className - Additional CSS class names
 * @param {string} props.testId - Test ID for testing
 * @param {string} props.ariaLabel - Accessible label for the badge
 * @returns {JSX.Element} The rendered badge component
 */
const LytBadge = ({
    label = undefined,
    value = undefined,
    type = BADGE_TYPES.DEFAULT,
    leftIcon = null,
    className = '',
    testId = null,
    ariaLabel = null,
    ...restProps
}) => {
    // Determine display content - label takes precedence
    const displayValue =
        label !== undefined && label !== null ? label : formatBadgeValue(value);
    const badgeProps = {
        className,
        'data-testid': testId,
        'aria-label':
            ariaLabel ||
            (label ? `${label} badge` : `Badge with value ${displayValue}`),
        badgeType: type,
        hasLabel: label !== undefined && label !== null,
        role: 'status',
        ...restProps,
    };
    return (
        <StyledBadge {...badgeProps}>
            {leftIcon && <LytIcon name={leftIcon} size={12} />}
            {displayValue}
        </StyledBadge>
    );
};
LytBadge.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.oneOf(Object.values(BADGE_TYPES)),
    leftIcon: PropTypes.oneOf(Object.values(ICON_NAMES)),
    className: PropTypes.string,
    testId: PropTypes.string,
    ariaLabel: PropTypes.string,
};
export default LytBadge;
